import { UserProperties } from '@futrli-sdk/users';
import { updateCurrentUserProperties } from '@futrli/views/mutations/currentUser';
import {
  QUERY_PARAMS,
  extractJsonFromBase64,
  getPaymentDetailsPageUrl,
  matchPaymentDetailsPagePath
} from '@futrli/shared-services';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, Location } from 'react-router-dom';
import queryString from 'query-string';

enum RedirectRoutesKeys {
  payment_form = 'payment_form'
}

interface RedirectPayload {
  workspaceId: string;
  userCreatedFromInvite?: boolean;
  redirect: keyof typeof RedirectRoutesKeys;
}

export interface RedirectInfo {
  payload?: RedirectPayload;
  path: string;
}

/**
 * React hook to read the go query param from url which will contain the
 * redirect data encoded in base64.
 * @param {Location['search']} locationSearch
 */
export const useGetRedirectInfoFromQueryParam = (locationSearch?: Location['search']) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = queryString.parse(locationSearch || location.search);
  const payloadBase64 = queryParams[QUERY_PARAMS.GO];
  const [redirectInfo, setRedirectInfo] = useState<RedirectInfo | null>(null);
  const [isLoadingRedirectInfo, setIsLoadingRedirectInfo] = useState<boolean>(true);

  useEffect(() => {
    try {
      const payload =
        typeof payloadBase64 === 'string' ? (extractJsonFromBase64(payloadBase64) as RedirectPayload) : undefined;
      const path = getRedirectPath(payload);

      if (!payload || !path) {
        setRedirectInfo(null);
      } else {
        setRedirectInfo({ payload, path });
      }
    } catch {
      setRedirectInfo(null);
    } finally {
      setIsLoadingRedirectInfo(false);
    }
  }, [payloadBase64]);

  const handleRedirect = async () => {
    if (isLoadingRedirectInfo || !redirectInfo) {
      return;
    }

    await setContextBeforeRedirecting(redirectInfo);
    navigate(redirectInfo.path);
  };

  return {
    isLoadingRedirectInfo,
    redirectInfo,
    onRedirect: handleRedirect
  };
};

export const getRedirectPath = (redirectData?: RedirectPayload) => {
  if (redirectData?.redirect === RedirectRoutesKeys.payment_form) {
    return getPaymentDetailsPageUrl(redirectData.workspaceId);
  }

  return;
};

/**
 * Sets context needed before redirecting the user.
 * @param {string} pathToRedirect
 */
export const setContextBeforeRedirecting = async ({ path, payload }: RedirectInfo) => {
  if (matchPaymentDetailsPagePath(path)) {
    const properties = [{
      property: UserProperties.redirect_to_payment_details,
      value: 'true'
    }];

    if (!payload?.userCreatedFromInvite) {
      properties.push({
        property: UserProperties.skip_redirect_to_integrations_onboarding,
        value: 'true'
      });
    }
    await updateCurrentUserProperties(properties);
  }

  return;
};
